import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/components/ui/use-toast';
import { trpc } from './trpc';

/**
 * Merchant hooks for on-chain crypto payments (crypto-api), over `trpc.cryptoOnchain.*`.
 *
 * The DTO shapes below mirror CONTRACT-monorepo-api §A so the components type-check against a stable
 * contract; once `@tagadapay/trpc-types` is rebuilt with the `cryptoOnchain` router, the inferred
 * outputs must stay assignable to them.
 */

/**
 * `not_enabled`: no link row. The others mirror the link row: `provisioning` while crypto-api is being
 * called, `failed` when that call did not go through (the row is kept and re-enable reuses it).
 */
export type CryptoOnchainStoreLinkStatus = 'not_enabled' | 'provisioning' | 'active' | 'disabled' | 'failed';

export type CryptoOnchainPaymentStatus =
  | 'pending'
  | 'detected'
  | 'paid'
  | 'paid_late'
  | 'underpaid'
  | 'overpaid'
  | 'expired'
  | 'failed';

export type CryptoOnchainPayoutAddressKind = 'evm' | 'solana' | 'tron' | 'btc' | 'ltc' | 'doge';
/** Kinds whose testnets pay a wallet of their own (their addresses carry the network). */
export type CryptoOnchainTestnetAddressKind = 'btc' | 'ltc' | 'doge';

/**
 * The networks and coins the checkout offers, per network (`{ [chainId]: [symbols] }`): the mainnets,
 * and the testnets of sandbox mode. Null on a network never picked: every network with a wallet.
 */
export interface CryptoOnchainSelectedAssets {
  mainnet: Record<string, string[]> | null;
  testnet: Record<string, string[]> | null;
}

/** One network a store can pick for the checkout; a `comingSoon` one is listed, never pickable. */
export interface CryptoOnchainNetwork {
  chainId: number;
  name: string;
  kind: string;
  testnet: boolean;
  assets: { symbol: string; stable: boolean }[];
  comingSoon: boolean;
}

export interface CryptoOnchainStoreStatus {
  platformAvailable: boolean;
  enabled: boolean;
  status: CryptoOnchainStoreLinkStatus;
  payoutAddresses: Partial<Record<CryptoOnchainPayoutAddressKind, string>>;
  /** Share of each payment kept by the platform, in percent; read-only for merchants. */
  effectiveSharePercent: number | null;
  cryptoApiMerchantId: string | null;
  /** Sandbox mode: the checkout offers test networks only. Null when crypto-api could not be read. */
  sandbox: boolean | null;
  /** Wallets on the Bitcoin, Litecoin and Dogecoin testnets; null when crypto-api could not be read. */
  testnetPayoutAddresses: Partial<Record<CryptoOnchainTestnetAddressKind, string>> | null;
  /** The networks picked per network; null when crypto-api could not be read. */
  selectedAssets: CryptoOnchainSelectedAssets | null;
  /** ISO date. */
  enabledAt: string | null;
  webhookUrl: string | null;
  /** Why the last enable attempt did not go through (status `failed`); null otherwise. */
  lastError: string | null;
}

export interface CryptoOnchainSettlement {
  totalBase: string;
  merchantBase: string;
  platformBase: string | null;
  platformBps: number | null;
  sweepTxHash: string | null;
  platformTxHash: string | null;
  waivedReason: string | null;
}

export interface CryptoOnchainPayment {
  id: string;
  orderId: string;
  paymentId: string | null;
  status: CryptoOnchainPaymentStatus;
  chainId: number | null;
  chainName: string | null;
  asset: string | null;
  tokenAddress: string | null;
  amountBase: string | null;
  amountDecimals: number | null;
  amountFormatted: string | null;
  fiatAmount: string | null;
  fiatCurrency: string | null;
  originalAmount: string | null;
  originalCurrency: string | null;
  txHash: string | null;
  /** Block explorer link of `txHash`, from crypto-api. */
  explorerTxUrl: string | null;
  depositAddress: string | null;
  paymentUri: string | null;
  paidAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  settlement: CryptoOnchainSettlement | null;
  /**
   * Paid after the order was already paid by another payment: the funds were received and recorded,
   * the order was left untouched, and the customer must be refunded by hand.
   */
  duplicate: boolean;
  /** Paid on a testnet (sandbox mode): no real money moved. */
  test: boolean;
  /** A testnet payment that settled after the store left sandbox mode: the order was not paid. */
  rejected: boolean;
}

export interface CryptoOnchainPaymentsPage {
  items: CryptoOnchainPayment[];
  nextCursor: string | null;
}

interface CryptoOnchainClientError {
  message: string;
  data?: { businessCode?: string | null } | null;
}

/**
 * The toast text for a refused mutation: the use case's business code (`error.data.businessCode`)
 * picks a translated sentence; anything else shows the server message.
 */
export function describeCryptoOnchainError(
  t: TFunction<'integrations'>,
  error: CryptoOnchainClientError,
): string {
  switch (error.data?.businessCode) {
    case 'PLATFORM_DISABLED':
      return t('cryptoOnchain.errors.platformDisabled');
    case 'PAYOUT_ADDRESS_REQUIRED':
      return t('cryptoOnchain.errors.payoutAddressRequired');
    case 'NO_NETWORK_ENABLED':
      return t('cryptoOnchain.errors.noNetworkEnabled');
    case 'INVALID_PAYOUT_ADDRESS':
      return t('cryptoOnchain.errors.invalidPayoutAddress', { message: error.message });
    case 'CRYPTO_API_UNAVAILABLE':
      return t('cryptoOnchain.errors.cryptoApiUnavailable');
    case 'NOT_ENABLED':
      return t('cryptoOnchain.errors.notEnabled');
    case 'STORE_NOT_FOUND':
      return t('cryptoOnchain.errors.storeNotFound');
    case 'PAYMENT_NOT_FOUND':
      return t('cryptoOnchain.errors.paymentNotFound');
    default:
      return error.message;
  }
}

export function useCryptoOnchainStoreStatus(storeId: string) {
  return trpc.cryptoOnchain.getStoreStatus.useQuery(
    { storeId },
    { enabled: !!storeId, staleTime: 30 * 1000 },
  );
}

/** The networks and coins a store can offer: the deployment's catalog, read once per form. */
export function useCryptoOnchainNetworks(storeId: string) {
  return trpc.cryptoOnchain.listNetworks.useQuery({ storeId }, { enabled: !!storeId, staleTime: 10 * 60 * 1000 });
}

export function useEnableCryptoOnchain() {
  const { toast } = useToast();
  const { t } = useTranslation('integrations');
  const utils = trpc.useUtils();
  return trpc.cryptoOnchain.enable.useMutation({
    onSuccess: (status, input) => {
      // The answer is the new status: the form restarts from it at once, before the refetch lands.
      utils.cryptoOnchain.getStoreStatus.setData({ storeId: input.storeId }, status);
      utils.cryptoOnchain.getStoreStatus.invalidate();
      utils.cryptoOnchain.listPayments.invalidate();
      // The enable use case also upserts the `integrations` row, which drives the grid badge.
      utils.integrations.list.invalidate();
      toast({
        title: t('cryptoOnchain.toasts.enabledTitle'),
        description: t('cryptoOnchain.toasts.enabledDescription'),
      });
    },
    onError: (error: CryptoOnchainClientError) => {
      toast({
        title: t('cryptoOnchain.toasts.enableFailedTitle'),
        description: describeCryptoOnchainError(t, error),
        variant: 'destructive',
      });
    },
  });
}

/**
 * Switches sandbox mode on an enabled store. Same procedure as the wallets, with no wallet change:
 * crypto-api re-derives the networks the checkout offers (testnets in sandbox, mainnets otherwise).
 */
export function useSetCryptoOnchainSandbox() {
  const { toast } = useToast();
  const { t } = useTranslation('integrations');
  const utils = trpc.useUtils();
  return trpc.cryptoOnchain.updatePayoutAddresses.useMutation({
    onSuccess: (status) => {
      utils.cryptoOnchain.getStoreStatus.invalidate();
      toast({
        title: status.sandbox ? t('cryptoOnchain.toasts.sandboxOnTitle') : t('cryptoOnchain.toasts.sandboxOffTitle'),
        description: status.sandbox
          ? t('cryptoOnchain.toasts.sandboxOnDescription')
          : t('cryptoOnchain.toasts.sandboxOffDescription'),
      });
    },
    onError: (error: CryptoOnchainClientError) => {
      toast({
        title: t('cryptoOnchain.toasts.sandboxFailedTitle'),
        description: describeCryptoOnchainError(t, error),
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateCryptoOnchainPayoutAddresses() {
  const { toast } = useToast();
  const { t } = useTranslation('integrations');
  const utils = trpc.useUtils();
  return trpc.cryptoOnchain.updatePayoutAddresses.useMutation({
    onSuccess: (status, input) => {
      utils.cryptoOnchain.getStoreStatus.setData({ storeId: input.storeId }, status);
      utils.cryptoOnchain.getStoreStatus.invalidate();
      toast({
        title: t('cryptoOnchain.toasts.updatedTitle'),
        description: t('cryptoOnchain.toasts.updatedDescription'),
      });
    },
    onError: (error: CryptoOnchainClientError) => {
      toast({
        title: t('cryptoOnchain.toasts.updateFailedTitle'),
        description: describeCryptoOnchainError(t, error),
        variant: 'destructive',
      });
    },
  });
}

export function useDisableCryptoOnchain() {
  const { toast } = useToast();
  const { t } = useTranslation('integrations');
  const utils = trpc.useUtils();
  return trpc.cryptoOnchain.disable.useMutation({
    onSuccess: () => {
      utils.cryptoOnchain.getStoreStatus.invalidate();
      utils.integrations.list.invalidate();
      toast({
        title: t('cryptoOnchain.toasts.disabledTitle'),
        description: t('cryptoOnchain.toasts.disabledDescription'),
      });
    },
    onError: (error: CryptoOnchainClientError) => {
      toast({
        title: t('cryptoOnchain.toasts.disableFailedTitle'),
        description: describeCryptoOnchainError(t, error),
        variant: 'destructive',
      });
    },
  });
}

export function useCryptoOnchainPayments(storeId: string, limit = 10, opts?: { enabled?: boolean }) {
  return trpc.cryptoOnchain.listPayments.useQuery(
    { storeId, limit },
    { enabled: (opts?.enabled ?? true) && !!storeId, staleTime: 15 * 1000 },
  );
}

/**
 * "Check status": asks the server to fetch the crypto-api session and reconcile the payment (a lost
 * webhook, a late payment). A mutation because it may complete the order; the list refetches after.
 */
export function useCheckCryptoOnchainPayment() {
  const { toast } = useToast();
  const { t } = useTranslation('integrations');
  const utils = trpc.useUtils();
  return trpc.cryptoOnchain.checkPayment.useMutation({
    onSuccess: () => {
      utils.cryptoOnchain.listPayments.invalidate();
      toast({
        title: t('cryptoOnchain.toasts.checkedTitle'),
        description: t('cryptoOnchain.toasts.checkedDescription'),
      });
    },
    onError: (error: CryptoOnchainClientError) => {
      toast({
        title: t('cryptoOnchain.toasts.checkFailedTitle'),
        description: describeCryptoOnchainError(t, error),
        variant: 'destructive',
      });
    },
  });
}
